// S1 — the onboarding wizard shell (Module 02 §4), on the UX-1 register: the
// AppShell provides the chrome (sidebar + topbar), this layout renders the ONE
// page-head row (§3.2 — breadcrumb current segment as the h1-equivalent), the
// session/live/target-date meta, and the §3.10 Stepper — the setup-card list
// grammar, one Stepper per screen — before handing to the routed step.
//
// One fetch drives all of it: `GET /v1/onboarding/session` returns the session,
// all four step states AND the `checklist_summary`, so the shell never needs
// the full checklist. The endpoint auto-creates the session on first call, so a
// brand-new tenant loads a `draft` session instead of a 404.
//
// §4 request states: loading = skeletons, error = retry card with request id,
// both from `components/states.tsx` (shared, never re-invented).

import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Banner } from "../../components/toast";
import { Button } from "../../components/button";
import { Card } from "../../components/card";
import { PageHeader } from "../../components/breadcrumbs";
import { Stepper, type StepperStep } from "../../components/stepper";
import { CardSkeleton, EmptyState, ErrorState } from "../../components/states";
import { formatBusinessDate } from "../../lib/datetime";
import { UnauthorizedError } from "../../lib/problem";
import { requestIdOf } from "../../lib/request";
import { LiveBadge, SessionStatusBadge } from "./components/wizard-badges";
import { useOnboardingSession, usePatchOnboardingSession } from "./hooks/onboarding-queries";
import { useOnboardingPermissions } from "./hooks/permissions";
import { problemStatus } from "./lib/wizard-problems";
import { WIZARD_STEPS, stepStatusOf, type StepNo } from "./lib/wizard-steps";
import "./onboarding.css";

/** Which step's screen is open, for the Stepper's "you are here" marker. */
function activeStepFromPath(pathname: string): StepNo | undefined {
  const match = WIZARD_STEPS.find((def) => def.to !== null && pathname.startsWith(def.to));
  return match?.stepNo;
}

export function OnboardingLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useOnboardingPermissions();
  const session = useOnboardingSession();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // 401 → sign-in, matching every other area.
  useEffect(() => {
    if (session.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [session.error, navigate]);

  // Permissions gate the SHELL, not just its contents: the page head renders
  // the terminology link only for `onboarding.edit_terminology`, and the hook
  // reports every code as false until /v1/auth/tenants answers. Paint before
  // that and the link appears late, widening `.ds-pagehead-right` after first
  // paint. Found 2026-08-10 on deployed staging (CLS 0.0004 @143ms) while the
  // replica stayed green twice over, because there the query resolves before
  // the first frame — the environment-latency class. Waiting here is what
  // makes the render-once contract (design-system §5) hold on a real network.
  if (session.isPending || !perms.isResolved) {
    return (
      <div className="onboarding-area">
        <CardSkeleton lines={5} label={t("onboarding.shell.loading")} />
      </div>
    );
  }

  // §7 Gap: a role without `onboarding.view` (floor_worker holds none) got the
  // generic retry-with-request-id card, which invites them to retry something
  // that will never succeed. The server boundary held; the UI state was wrong.
  if (session.error !== null && problemStatus(session.error) === 403) {
    return (
      <div className="onboarding-area">
        <div role="status" data-testid="onboarding-forbidden">
          <EmptyState
            title={t("onboarding.shell.forbiddenTitle")}
            body={t("onboarding.shell.forbiddenBody")}
          />
        </div>
      </div>
    );
  }

  if (session.error !== null) {
    return (
      <div className="onboarding-area">
        <ErrorState
          title={t("onboarding.shell.errorTitle")}
          detail={t("onboarding.shell.errorDetail")}
          retryLabel={t("onboarding.shell.retry")}
          onRetry={() => void session.refetch()}
          requestId={requestIdOf(session.error)}
          requestIdLabel={t("onboarding.shell.requestId")}
        />
      </div>
    );
  }

  const { session: row, steps } = session.data;
  const isLive = row.status === "live";
  const activeStep = activeStepFromPath(pathname);
  const activeDef = WIZARD_STEPS.find((d) => d.stepNo === activeStep);
  const isOverview = pathname.replace(/\/+$/, "") === "/onboarding";

  // FROZEN HISTORY (§3.1, B3): a `live` session shows the steps it went live
  // with — a tenant who went live under the four-step wizard must not read a
  // phantom "Quality checks — not started" it can never complete. Pre-live,
  // the static registry is the truth (GET /session materialises any appended
  // step's row lazily, so the two sources agree).
  const railSteps = isLive
    ? WIZARD_STEPS.filter((def) => steps.some((s) => s.step_no === def.stepNo))
    : WIZARD_STEPS;
  const stepperSteps: StepperStep[] = railSteps.map((def) => {
    const status = stepStatusOf(steps, def.stepNo);
    const isHere =
      activeStep === def.stepNo || (activeStep === undefined && row.current_step === def.stepNo);
    return {
      key: String(def.stepNo),
      label: t(def.titleKey),
      state: status === "completed" ? "done" : isHere ? "current" : "todo",
      meta: t(`onboarding.stepStatus.${status}`),
      to: def.to ?? undefined,
      "data-testid": `wizard-step-${def.stepNo}`,
      "data-status": status,
    };
  });

  return (
    <div className="onboarding-area" data-session-status={row.status}>
      <PageHeader
        title={activeDef !== undefined ? t(activeDef.titleKey) : t("onboarding.shell.title")}
        {...(activeDef !== undefined
          ? { crumbs: [{ label: t("onboarding.shell.title"), to: "/onboarding" }] }
          : {})}
        actions={
          <>
            <Link to="/onboarding/checklist" className="ds-lnk">
              {t("onboarding.checklist.open")}
            </Link>
            {/* Terminology settings are group_admin-only (§7); the route stays
                reachable for a deep link and answers 403 itself. */}
            {perms.has("onboarding.edit_terminology") ? (
              <Link to="/settings/terminology" className="ds-lnk">
                {t("onboarding.home.settingsLink")}
              </Link>
            ) : null}
          </>
        }
      />

      <p className="wizard-meta">
        {/* A live session's status IS the live badge; showing both printed
            "Live Live" side by side. The badge carries the go-live date. */}
        {isLive ? (
          <LiveBadge wentLiveAt={row.went_live_at} />
        ) : (
          <SessionStatusBadge status={row.status} />
        )}
        <TargetGoLiveDate
          value={row.target_go_live_date}
          canEdit={perms.has("onboarding.manage_wizard") && !isLive}
        />
      </p>

      {isLive ? (
        <Banner kind="neutral" data-testid="wizard-frozen">
          {t("onboarding.shell.frozenNotice")}
        </Banner>
      ) : null}

      {/* On the S1 overview the rail is the page's main content, so it takes
          the wide column (step names were cut off at 250 px) and the resume
          and completeness cards stack beside it. Every step keeps the narrow
          rail beside its form. */}
      <div className={isOverview ? "wizard-grid wizard-grid-overview" : "wizard-grid"}>
        <Card title={t("onboarding.shell.stepRailLabel")} className="wizard-steps-card">
          <Stepper steps={stepperSteps} data-testid="wizard-stepper" />
        </Card>

        <div className="wizard-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/**
 * The target go-live date (§5 `PATCH /v1/onboarding/session`) — an inline
 * editor for holders of `onboarding.manage_wizard`, read-only for everyone
 * else and after go-live (when the API answers 409 ALREADY_LIVE).
 */
function TargetGoLiveDate({ value, canEdit }: { value: string | null; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const patch = usePatchOnboardingSession();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  // The wire form is a `date` business date; the READ form is the locale's, via
  // the one app-wide formatter. Printing the raw `2026-07-15` put a third date
  // format on a screen that already carried a native picker and a timestamp
  // table (surface review F12) — and the ISO string is the one nobody reads.
  const shown =
    value !== null && value.length > 0
      ? formatBusinessDate(value, i18n.language)
      : t("onboarding.shell.targetGoLiveUnset");

  if (!editing) {
    return (
      <span className="wizard-target-date num">
        {t("onboarding.shell.targetGoLive")}: {shown}
        {canEdit ? (
          <button
            type="button"
            className="ds-lnk"
            data-testid="target-date-edit"
            onClick={() => {
              setDraft(value ?? "");
              setEditing(true);
            }}
          >
            {value !== null && value.length > 0
              ? t("onboarding.shell.targetGoLiveChange")
              : t("onboarding.shell.targetGoLiveSet")}
          </button>
        ) : null}
      </span>
    );
  }

  return (
    <span className="wizard-target-date">
      <label className="visually-hidden" htmlFor="wizard-target-date">
        {t("onboarding.shell.targetGoLive")}
      </label>
      <input
        id="wizard-target-date"
        className="ds-input"
        type="date"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
        }}
      />
      <Button
        size="sm"
        busy={patch.isPending}
        onClick={() => {
          patch.mutate(
            // Clearing the field is a real intent, and the contract takes null.
            { target_go_live_date: draft.length > 0 ? draft : null },
            {
              onSuccess: () => {
                setEditing(false);
              },
            },
          );
        }}
      >
        {patch.isPending ? t("onboarding.save.saving") : t("onboarding.save.save")}
      </Button>
      <Button
        size="sm"
        onClick={() => {
          setEditing(false);
          patch.reset();
        }}
      >
        {t("onboarding.goLive.cancel")}
      </Button>
      {patch.error !== null ? (
        <span className="wizard-target-date-error" role="alert">
          {t("onboarding.shell.targetGoLiveFailed")}
        </span>
      ) : null}
    </span>
  );
}
